import { useRef } from "react";
import { toPng } from "html-to-image";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Cupom from "./Cupom";
import { ICupomEvento } from "interfaces/cupom";
import { setMessage, useMaterialUIController } from "context";
import { modalHeaderSx, modalPaperSx, saveButtonSx } from "../cupons.theme";

interface Props {
  open: boolean;
  onClose: () => void;
  data: ICupomEvento | null;
}

export default function CupomPreviewDialog({ open, onClose, data }: Props) {
  const [, dispatch] = useMaterialUIController();
  const cupomRef = useRef<HTMLDivElement>(null);

  const handleDownload = async () => {
    if (!cupomRef.current || !data) return;

    try {
      const dataUrl = await toPng(cupomRef.current, {
        cacheBust: true,
        pixelRatio: 2,
      });
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = `cupom-${data.codigoPromocional.toUpperCase()}.png`;
      link.click();
      link.remove();
      setMessage(dispatch, { open: true, text: "Cupom baixado com sucesso!" });
    } catch (err) {
      console.error("Erro ao baixar o cupom:", err);
      setMessage(dispatch, {
        open: true,
        text: "Erro ao baixar o cupom. Tente novamente.",
        severity: "error",
      });
    }
  };

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: { ...modalPaperSx, maxWidth: 480 },
      }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Stack direction="row" spacing={2} alignItems="center" minWidth={0}>
            <ConfirmationNumberOutlinedIcon sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box minWidth={0}>
              <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1.2 }}>
                Pré-visualização
              </Typography>
              <Typography variant="h5" fontWeight="bold" noWrap>
                {data.codigoPromocional.toUpperCase()}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.25 }} noWrap>
                {data.evento?.titulo}
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={{ py: 3, display: "flex", justifyContent: "center" }}>
        <MDBox
          ref={cupomRef}
          sx={{ "& > *": { transform: "scale(0.9)", transformOrigin: "center" } }}
        >
          <Cupom data={data} />
        </MDBox>
      </DialogContent>

      <MDBox
        px={3}
        py={2}
        display="flex"
        gap={1}
        justifyContent="flex-end"
        borderTop="1px solid"
        borderColor="grey.200"
      >
        <MDButton type="button" variant="outlined" color="dark" onClick={onClose}>
          Fechar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          startIcon={<DownloadOutlinedIcon />}
          onClick={() => void handleDownload()}
          sx={saveButtonSx}
        >
          Baixar cupom
        </MDButton>
      </MDBox>
    </Dialog>
  );
}
